import { Component, OnInit, ViewChild } from '@angular/core';
import { AuthService, BaseComponent } from '@nstep-common/core';
import { ModalComponent } from '@nstep-common/semantic-ui';
import { toast } from '@nstep-common/utils';
import { OnlineTransactionActionModel, PurchaseTransactionsModel, AllowedDto, EntitlementTransactionDtoModel, ExtendedPOSLocationDto, PurchaseBalanceDto } from '@nstep-public/pages';
import { AmisCardService, CancelTransactionComponent, OnlineTransactionService, OperatorService } from '@nstep-public/shared';
import { chain, flatten } from 'lodash';

@Component({
	selector: 'app-online-transactions',
	templateUrl: './online-transactions.component.html',
	standalone: false
})
export class OnlineTransactionsComponent extends BaseComponent implements OnInit {

	@ViewChild(CancelTransactionComponent) cancelTransactionModal!: CancelTransactionComponent;
	@ViewChild('accessDeniedModal') accessDeniedModal!: ModalComponent;
	currentDate = new Date();
	currentPosLocationInfo!: ExtendedPOSLocationDto;

	tabs = ['searchCard', 'nonRationedTypes', 'rationedTypes', 'confirmTransaction', 'insertPIN'];
	currentTab = this.tabs[0];
	purchaseBalanceData!: PurchaseBalanceDto;
	nonRationedItems: AllowedDto[] = [];

	rationedItems: AllowedDto[] = [];
	rationedItemsBackup: AllowedDto[] = [];

	attempts = 0;

	loading = false;

	sentTransaction = false;
	transactionDate = '';

	constructor(private authService: AuthService,
		private onlineTransactionService: OnlineTransactionService,
		private amisCardService: AmisCardService,
		private operatorService: OperatorService) {
		super();

		this.currentPosLocationInfo = this.operatorService.getLocationInfo()!;
		setInterval(() => this.currentDate = new Date(), 1000);
	}

	search(amisCardNumber: string): void {
		this.loading = true;

		this.subscriptions.push(
			this.onlineTransactionService.getPurchaseBalance(amisCardNumber, this.currentPosLocationInfo.location.id).subscribe({
				next: response => {
					this.purchaseBalanceData = response;

					this.sentTransaction = false;
					this.transactionDate = new Date().toISOString();

					this.purchaseBalanceData.allowed.forEach(item => {
						if (!item.rationed) {
							item.available = true;
							this.nonRationedItems.push(item);
						} else {
							item.parsedBalance = parseInt(item.balance);
							item.transactionAmount = 0;
							this.rationedItems.push(item);
						}
					});

					this.purchaseBalanceData.forbidden.forEach(item => {
						if (!item.rationed) {

							this.nonRationedItems.push({ rationed: item.rationed, entitlementType: item.entitlementType, available: item.available } as AllowedDto);
						}
					});

					this.nonRationedItems = chain(this.nonRationedItems)
						.orderBy(d => d.entitlementType)
						.value();

					this.rationedItems = chain(this.rationedItems)
						.orderBy(d => d.entitlementType)
						.value();

					this.loading = false;
					this.currentTab = this.tabs[1];
					this.attempts = 1;
				},
				error: (response) => {
					this.loading = false;

					const errors: string[] = flatten(Object.values(response));

					errors.forEach(error => {
						toast('Error', error, 'red');
					});
				}
			})
		);
	}

	onAction(model: OnlineTransactionActionModel): void {

		if (model.action === 'cancel') {
			if (model.currentTabIndex === 1) {
				this.cancelTransaction();
			} else if (model.currentTabIndex === 2) {
				this.cancelTransactionModal.toggle();
			} else if (model.currentTabIndex === 3) {
				this.rationedItems = JSON.parse(JSON.stringify(this.rationedItemsBackup));
				this.currentTab = this.tabs[model.currentTabIndex - 1];
			} else {
				this.currentTab = this.tabs[model.currentTabIndex - 1];
			}
		} else {
			if (model.currentTabIndex === 2) {
				this.rationedItemsBackup = JSON.parse(JSON.stringify(this.rationedItems));
				this.rationedItems = this.rationedItems.filter(e => e.transactionAmount > 0);
				this.currentTab = this.tabs[model.currentTabIndex + 1];
			} else if (model.currentTabIndex === 4) {
				this.makeTransactions(model.pin!);
			} else {
				this.currentTab = this.tabs[model.currentTabIndex + 1];
			}
		}
	}

	cancelTransaction(): void {
		this.rationedItems = [];
		this.nonRationedItems = [];
		this.amisCardService.changeAmisCardNumber('');
		this.currentTab = this.tabs[0];
		this.sentTransaction = false;
	}

	makeTransactions(pin: string): void {
		if (this.sentTransaction) {
			toast('Warning', 'The transaction has already been made.', 'orange');
			return;
		}

		this.sentTransaction = true;
		this.loading = true;

		const dataToSave = chain(this.rationedItems)
			.map(e => new EntitlementTransactionDtoModel({
				amiscardId: e.amisCardId,
				entitlementCodeId: e.entitlementCodeId,
				entitlementTypeId: e.entitlementTypeId,
				measurementUnitAssociationId: e.measurementUnitId,
				transactionAmount: e.transactionAmount,
				deviceTransactionId: crypto.randomUUID(),
				timeStamp: this.transactionDate
			}))
			.value();

		const purchaseModel: PurchaseTransactionsModel = {
			locationId: this.currentPosLocationInfo.location.id,
			cardNumber: this.purchaseBalanceData.card.cardNumber,
			pin: pin,
			transactions: dataToSave
		};

		this.subscriptions.push(
			this.onlineTransactionService.createPurchaseTransactions(purchaseModel).subscribe({
				next: () => {
					toast('Success', 'The transaction was successfully completed.', 'green');
					this.cancelTransaction();
					this.onlineTransactionService.clearOnlineTransactionsCache();
					this.loading = false;
				},
				error: (response: any) => {
					this.sentTransaction = false;

					const errors: string[] = flatten(Object.values(response));

					if (errors.some(d => d === 'The PIN is incorrect')) {
						this.attempts += 1;
						this.onlineTransactionService.changePin('');
					} else if (errors.some(d => d.startsWith('Access to '))) {
						this.accessDeniedModal.toggle();
					} else {
						toast('Warning', errors[0], 'orange');
					}

					if (this.attempts > 3) {
						this.cancelTransaction();
					}

					this.loading = false;
				}
			})
		);
	}

	logout(): void {
		this.accessDeniedModal.toggle();
		this.authService.logOut();
	}
}
